import { decodeHtmlEntities } from './decodeHtmlEntities';

/** 수식 하나를 글자수 계산에서 대신하는 자리표시 문자(OBJECT REPLACEMENT CHARACTER). */
const FORMULA_PLACEHOLDER = '\uFFFC';

/**
 * 수식 하나가 글자수 제한에서 차지하는 글자수.
 * 서버 `QuizDocMapper` 의 `FORMULA_WEIGHT` 와 같은 값으로 함께 관리한다 — 다르면 클라 검증을
 * 통과한 보기가 저장 요청에서 거절된다.
 */
export const FORMULA_WEIGHT = 1;

/**
 * 짝맞추기·순서맞추기 보기에서 수식 하나의 LaTeX 원문 상한. 수식은 1자로 세므로, 이 상한이
 * 없으면 수식 하나가 끝없이 길어져 칸을 넘친다. 서버 `QuizDocMapper` 와 같은 값으로 함께 관리한다.
 */
export const ITEM_FORMULA_LATEX_MAX_LENGTH = 100;

const ZWSP_FORMULA_PATTERN = /\u200B([^\u200B]*)\u200B/g;
const ZWSP_PATTERN = /\u200B/g;
// JS `\s` 는 전각 공백(U+3000) 같은 유니코드 공백까지 포함해 서버(Kotlin `\s`, ASCII 한정)와
// 결과가 갈린다. 서버와 같은 문자 집합을 직접 적는다.
const WHITESPACE_PATTERN = /[ \t\n\r\f\v\u00A0]+/g;

const normalizeVisibleText = (value: string) =>
  value.replace(ZWSP_PATTERN, '').replace(WHITESPACE_PATTERN, ' ').trim();

// 공백뿐인 수식은 화면에 아무것도 없으므로 0자로 센다(서버도 이 보기를 '내용 없음'으로 본다).
const toFormulaToken = (_match: string, latex: string) =>
  normalizeVisibleText(latex) ? FORMULA_PLACEHOLDER : '';

const countVisibleText = (text: string): number => {
  const countable = normalizeVisibleText(text);
  const formulaCount = countable.split(FORMULA_PLACEHOLDER).length - 1;

  return countable.length - formulaCount + formulaCount * FORMULA_WEIGHT;
};

/**
 * 보기의 수식 중 LaTeX 원문이 `maxLength` 를 넘는 것이 있는지. `text` 안의 수식(ZWSP 쌍)과
 * `equation` 필드(옛 데이터·AI·문제은행이 수식을 따로 담는 칸)를 함께 본다 — 서버도 둘 다 ZWSP 를
 * 뺀 길이로 같은 상한에 건다.
 */
export const hasFormulaOverLength = (
  { text, equation }: { text: string; equation?: string },
  maxLength: number,
) =>
  [
    ...[...decodeHtmlEntities(text).matchAll(ZWSP_FORMULA_PATTERN)].map(
      ([, latex]) => latex,
    ),
    ...(equation === undefined ? [] : [equation.replace(ZWSP_PATTERN, '')]),
  ].some(latex => latex.length > maxLength);

/**
 * 저장값 `text`(`extractBareText` 결과)의 글자수. `sanitize-html` 이 인코딩한 엔티티를 풀고,
 * ZWSP 로 감싼 수식을 1개당 `FORMULA_WEIGHT` 자로, 연속 공백은 1자로, 앞뒤 공백은 0자로 센다.
 * zod 검증이 쓴다. 입력 중 제한은 에디터 기본 카운터가 맡는다(`useBuildTextEditor`).
 *
 * 서버도 저장 요청을 받아 같은 규칙으로 다시 센다(짝맞추기·순서맞추기 보기). 두 구현은 함께
 * 관리된다 — zep-apps `QuizDocMapper.visibleItemContents`.
 */
export const countQuizText = (text: string): number =>
  countVisibleText(
    decodeHtmlEntities(text).replace(ZWSP_FORMULA_PATTERN, toFormulaToken),
  );
